import { v4 as uuid } from 'uuid';

import { referrers, sources } from 'tracking/params';
import { trackEventFaro } from 'utils/faro';

export interface TrialAndStackRef {
  id: number;
  slug: string;
}

export interface TrialAndStackStartStackRef extends TrialAndStackRef {
  url?: string;
  ready?: boolean;
}

export interface TrialAndStackStartResponse {
  org: TrialAndStackRef;
  stack: TrialAndStackStartStackRef;
}

export interface StackReadinessResponse {
  ready: boolean;
}

export enum Endpoints {
  ACCEPT_INVITE = '/invite/confirm',
  CHECK_INVITE = '/invite/confirm',
  GET_STACK_READINESS = '/v1/stacks',
  CHECK_USER_SSO = '/check-user-sso',
  CREATE_USER_FROM_INVITE = '/signup/invite',
  DASHBOARDS = '/dashboards',
  FORGOT_PASSWORD = '/forgotpw',
  GET_USER_PROFILE = '/profile',
  GET_INSTANCE = '/instances',
  GET_INSTANCES = '/instances',
  GET_ORGS = '/orgs',
  LOADING_INSTANCE = '/api/health',
  GET_ORG_SSO_PROVIDERS = '/invite/sso',
  GET_STACK_REGIONS = '/v1/stack-regions',
  GET_CLOSEST_STACK_REGION = '/v1/stack-regions/closest',
  RESEND_VALIDATION_EMAIL = '/signup/confirm/resend',
  SIGN_IN = '/login',
  SIGN_OUT = '/logout',
  SIGN_UP = '/signup',
  TRIAL_AND_STACK_START = '/v2/grafana-cloud/trial-and-stack/start',
  TRACK_TRIAL = '/grafana-cloud/start',
  VALIDATE_EMAIL_SHORT_KEY = '/signup/confirm-short-key',
  PLUGINS = '/plugins',
  UPDATE_USER_PROFILE = '/profile',
  GRAFANA_SSO_LOOKUP = '/login/grafana/sso/lookup',
  GRAFANA_SSO_LOGIN = '/login/grafana/sso/login',
  MFA_VERIFY_LOGIN = '/user/mfa/verify-login',
  MFA_REQUEST_RECOVERY_OTP = '/user/mfa/request-recovery-otp',
  MFA_VERIFY_RECOVERY_OTP = '/user/mfa/verify-recovery-otp',
  MFA_SETUP = '/user/mfa/setup',
  MFA_VERIFY_SETUP = '/user/mfa/verify-setup',
  MFA_STATUS = '/user/mfa/status',
  ACCEPT_LEGAL_TERMS = '/user/legal-terms-acceptances',
  LEGAL_VERSIONS = '/mw/api/v1/legal-versions/',
}

interface ApiError {
  code: string;
  message: string;
}

export interface MfaStatusResponse {
  available: boolean;
  enabled: boolean;
  method: 'totp' | null;
  recoveryCodesRemaining: number;
  lastUsed: string | null;
  required: boolean;
}

const getErrorFromResponse = async (res: Response): Promise<ApiError> => {
  let error;

  try {
    error = await res.clone().json();
  } catch {
    error = {
      code: res.statusText,
      message: await res.clone().text(),
    };
  }

  return error;
};

const request = async <R = any>(path: string, options: RequestInit = {}, unconstrictedFetch?: boolean): Promise<R> => {
  path = !unconstrictedFetch ? `/api${path}` : `${path}`;

  const requestId = uuid();
  const requestType = options.method || 'GET';

  trackEventFaro('api_call', {
    id: requestId,
    type: requestType,
    url: path,
  });

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) ?? {}),
    'X-Request-ID': requestId,
  };

  const { isource, osource } = sources;

  if (isource) {
    headers['X-ISource'] = isource;
  }

  if (osource) {
    headers['X-OSource'] = osource;
  }

  const { ireferrer, oreferrer } = referrers;

  if (ireferrer) {
    headers['X-IReferrer'] = ireferrer;
  }

  if (oreferrer) {
    headers['X-OReferrer'] = oreferrer;
  }

  return fetch(path, {
    ...options,
    headers,
  }).then(async (res) => {
    if (!res.ok) {
      const error = await getErrorFromResponse(res);

      trackEventFaro('api_call_failed', {
        id: requestId,
        type: requestType,
        url: path,
        ...error,
      });

      throw error;
    }

    trackEventFaro('api_call_success', {
      id: requestId,
      type: requestType,
      url: path,
    });

    return res.json();
  });
};

/** Pass `unconstrictedFetch=true` for paths outside grafana-com, e.g. {@link Endpoints.LEGAL_VERSIONS}. */
export const get = async <R = any>(path: string, unconstrictedFetch = false): Promise<R> =>
  request<R>(path, {}, unconstrictedFetch);

export const post = async <R = any>(path: string, data: Record<string, any>): Promise<R> =>
  request<R>(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

export const retry = async <R = any>(fn: () => Promise<R>, retries: number, timeWaited: number): Promise<R> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, timeWaited));
      return retry(fn, retries - 1, timeWaited * 2);
    } else {
      throw error;
    }
  }
};
